import Backbone from 'backbone'
import colorIcon from 'scoreboard/components/color-icon'
import _ from 'lodash'
import $ from 'jquery'
import Tooltip from 'scoreboard/components/tooltip'
import { getSerieLabelForGroupedBreakdown }  from 'scoreboard/charts/chart-data/prepare-data'
import prepareTableData from './chart-data/prepare-table-data'
import i18n from 'scoreboard/components/i18n'
import formatValue from 'scoreboard/components/format-value'
import { adjustLightness } from 'scoreboard-colors'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER } = DataType

var ColorLegend = Backbone.View.extend({
	className: 'chart-color-legend',

	initialize: function (opt) {
		this.options = opt
		this.show = _.pick(opt, 'tooltip', 'labels')
		this.chart = opt.chart
		this.series = this.chart ? this.getSeriesData() : opt.series
	},

	/**
	 * Extract serie information from the chart designer
	 */
	getSeriesData: function() {
		const hasGroupedRangesBreakdown = this.chart.hasGroupedRangesBreakdown()

		return this.chart.getFeatures({ type: 'serie' }).map(serie => {
				const { id, tempId } = _.pick(serie, 'id', 'tempId')

				// #42337 when using grouped ranges, set the label to reflect this
				const label = hasGroupedRangesBreakdown ?
					getSerieLabelForGroupedBreakdown({ chart: this.chart, underlyingSerie: serie }) :
					this.chart.featureLabel({ feature: serie, id, tempId })
				return {
					id: serie.get('id'),
					tempId: serie.get('tempId'),
					label: label,
					color: serie.get('color')
				}
			})
			.filter(function (s) {
				// if we couldn't determine a series label, leave it out; user probably
				// doesn't have permission to view it
				return !!s.label
			})
	},

	render: function () {
		let container = this.$el.closest('.chart-ratio-handler')
		let prevAlignment = container.data('legend-alignment')
		container.data('legend-alignment', this.options.legendType)
		container
			.removeClass(`legend-aligned-${prevAlignment}`)
			.addClass(`legend-aligned-${this.options.legendType}`)
		this.$el.addClass(this.options.legendType)

		var icons = (this.chart && this.chart.get('plotType') === 'filled-map')
			? this.getConditionalLegend() : this.getManualLegend()

		this.$el.html(icons)

		return this
	},

	getManualLegend: function() {
		let serieOrder = {}
		let serieIconColors
		if (this.chart) {
			let prepared = prepareTableData({
				chart: this.chart,
				chartData: this.chart.data,
				forChart: false
			})
			serieIconColors = prepared.serieIconColors
			let preparedSeries = _.get(prepared, 'preparedData.plots[0].series')
			if (preparedSeries) {
				preparedSeries.forEach((s, i) => {
					// this.series[].id will be the same as the underlyingSerieId
					// from the prepared data's series
					serieOrder[s.underlyingSerieId] = i
				})
			}
		} else {
			serieIconColors = this.series.map(s => s.color)
			// For initiative EVM charts, this.series is already in the correct order
			this.series.forEach((s, i) => {
				serieOrder[s.id] = i
			})
		}

		// this.series is sorted by sortOrder, but the chart may be configured
		// to sort series on something else (like by-value). Put the series
		// in the same order as the prepared data, and that's the order the
		// icons should be in.
		let sortedSeries = _.sortBy(this.series, s => serieOrder[s.id])
		return sortedSeries.map((serie, serieIndex) => {
			return this.buildSerieManualIcon(serie, serieIndex, serieIconColors)
		})
	},

	buildSerieManualIcon (serie, serieIndex, serieIconColors) {
		var s = serie.toJSON ? serie.toJSON() : serie

		var iconContainer = $('<span></span>')
		if (this.show.labels) {
			iconContainer.text(s.label)
		}

		var iconEl = $(colorIcon(serieIconColors[serieIndex], 'circle-fill'))
			.prependTo(iconContainer)

		this.addIconEvents(s, iconEl)

		return iconContainer
	},

	addIconEvents (s, iconEl) {
		var view = this

		function emphasize () {
			if (view.show.tooltip) {
				view._removeIconTooltip()
				view._iconTooltip = new Tooltip({
					content: '<h3 class="title">'
						+ _.escape(s.label) + '</h3>',
					attachTo: iconEl,
					placement: 'above',
					arrow: 'center',
					extraClass: 'color-legend-tooltip'
				}).render()
			}

			view.trigger('emphasize', { id: s.id, tempId: s.tempId })
		}

		function deemphasize () {
			if (view.show.tooltip) {
				view._removeIconTooltip()
			}

			view.trigger('deemphasize', null)
		}

		iconEl.mouseenter(emphasize)
		iconEl.mouseleave(deemphasize)

		function emphasizeOnClick () {
			emphasize()
			process.nextTick(function () {
				$(window).one('click', deemphasizeOnClick)
			})
		}

		function deemphasizeOnClick () {
			deemphasize()
			process.nextTick(function () {
				iconEl.one('click', emphasizeOnClick)
			})
		}

		iconEl.one('click', emphasizeOnClick)
	},

	getConditionalLegend: function() {
		const colorModel = this.chart.getConditionalColoringModel()
		if (_.isNil(colorModel)) return []

		let thresholdData = colorModel.getSectionsToColors()
		return thresholdData.map(function (data) {
			var iconContainer = $('<span></span>')

			if (this.show.labels) {
				iconContainer.html(`<span>${_.escape(this.conditionalLabel(data.thresholdPair))}</span>`)
			}

			let icon = $(colorIcon(data.color, { icon: 'circle-fill', fill: data.color }))
			icon.css({
				'border-color': adjustLightness(data.color, -20),
				'background': data.color
			})
			icon.prependTo(iconContainer)

			var view = this
			icon.mouseenter(function() {
				view.trigger('emphasize', data.color)
			})
			icon.mouseleave(function() {
				view.trigger('deemphasize', null)
			})

			return iconContainer
		}.bind(this))
	},

	conditionalLabel: function(datum) {
		const legend = this.chart.get('legends')[0]
		const precision = _.isEmpty(legend.precision) ?
			legend.precision : _.parseInt(legend.precision)

		let dataType = NUMBER
		const dataset = this.chart.startingDataset()
		const fieldId = _.get(this.chart.getSerieFeatures(),
			'[0].attributes.source.aggregation.fieldId')
		if (!_.isNil(fieldId) && !_.isNil(dataset)) {
			const field = dataset.getUnderlyingField(fieldId)
			if (!!field?.dataType) dataType = field.dataType
		}

		const fv = (val) => formatValue({
			value: val,
			dataType,
			shorten: legend.abbreviateLegendValues,
			precision
		})
		return datum[0] == datum[1] ? fv(datum[0]) :
			i18n.get('datasets.filters.pretty.to', fv(datum[0]),
			fv(datum[1]))
	},

	_removeIconTooltip: function () {
		this._iconTooltip && this._iconTooltip.remove()
		delete this._iconTooltip
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeIconTooltip()
	}
})

export default ColorLegend

ColorLegend.__filename__ = 'charts/color-legend.js'
